/* eslint-disable @typescript-eslint/no-explicit-any */
import { find, isEmpty, keyBy } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

import { CalendarItem } from 'utils/calendarItems';
import { OptionGroupInfo } from 'components/ticket-groups/MiniPurchaseSummary';
import { core, safeToNumber } from '@tock/coretypes';
import { dashboard } from 'types';
import { getActivePatronNoteScopeTypes } from 'constants/PatronNoteTypes';
import {
  getOptionGroupsForTicketGroup,
  organizeOptionsByOptionGroup,
} from 'utils/reservationDetailsUtils';
import TicketGroupStateConstants from 'constants/TicketGroupStateConstants';

export function getOptionGroupsForCalendarItem(
  ticketGroup: core.TicketGroup,
  calendarItem: CalendarItem
): OptionGroupInfo[] {
  const purchaseVerifyInfo = oc(
    calendarItem
  ).ticketSubset.purchaseVerifyInfo() as core.PurchaseVerifyInfo;
  if (purchaseVerifyInfo) {
    return getOptionGroupsForPurchaseVerify(purchaseVerifyInfo);
  } else {
    return getOptionGroupsForTicketGroup(ticketGroup);
  }
}

export function getOptionGroupsForPurchaseVerify(
  purchaseVerifyInfo: core.PurchaseVerifyInfo
): OptionGroupInfo[] {
  return organizeOptionsByOptionGroup(
    purchaseVerifyInfo.ticketOptionGroup as core.TicketOptionGroup[],
    purchaseVerifyInfo.option as core.Price[]
  );
}

export function visitGeneralNotes(ticketGroup: core.TicketGroup): dashboard.AuditedNote {
  return visitNotes(ticketGroup, 'GENERAL');
}

export function visitDietaryNotes(ticketGroup: core.TicketGroup): dashboard.AuditedNote {
  return visitNotes(ticketGroup, 'DIETARY');
}

/**
 * Get a dietary or general visit note object
 *
 * @param ticketGroup ticketGroup
 * @param noteType the relevant dashboard.NoteType ('GENERAL' | 'DIETARY')
 * @returns Note object
 */
function visitNotes(
  ticketGroup: core.TicketGroup,
  noteType: dashboard.NoteType
): dashboard.AuditedNote {
  return find(ticketGroup.auditedBookingNote, (note: any) => {
    return !isEmpty(note.note) && note.noteType === noteType;
  }) as dashboard.AuditedNote;
}

export function lockedVisitGeneralNotes(ticketSubset: core.TicketSubset) {
  return lockedVisitNotes(ticketSubset, 'GENERAL');
}

export function lockedVisitDietaryNotes(ticketSubset: core.TicketSubset) {
  return lockedVisitNotes(ticketSubset, 'DIETARY');
}

function lockedVisitNotes(ticketSubset: core.TicketSubset, noteType: dashboard.NoteType) {
  return (
    find(ticketSubset.auditedBookingNote, (note: any) => {
      return !isEmpty(note.note) && note.noteType === noteType;
    }) || undefined
  );
}

export function guestOrGroupGuestNotes(
  itemState: string,
  ticketGroup: core.TicketGroup,
  activeBusinessId: number
): dashboard.AuditedNote {
  if (guestNotes(itemState, ticketGroup, 'GENERAL', activeBusinessId)) {
    return guestNotes(itemState, ticketGroup, 'GENERAL', activeBusinessId);
  } else {
    return guestGroupNotes(itemState, ticketGroup, 'GENERAL');
  }
}

export function guestDietaryOrGroupGuestDietaryNotes(
  itemState: string,
  ticketGroup: core.TicketGroup,
  activeBusinessId: number,
  isFeatureEnabled: (flag: core.Feature.FeatureName) => boolean
): dashboard.AuditedNote {
  const noteScopes = keyBy(getActivePatronNoteScopeTypes(isFeatureEnabled), 'type');
  const dietaryNoteScope = noteScopes['DIETARY'].scope;

  if (dietaryNoteScope === 'BUSINESS') {
    return guestNotes(itemState, ticketGroup, 'DIETARY', activeBusinessId);
  } else {
    return guestGroupNotes(itemState, ticketGroup, 'DIETARY');
  }
}

/**
 * Get a dietary or general RESTAURANT LEVEL guest note object
 *
 * @param purchase PastPurchase
 * @param noteType string of 'GENERAL' or 'DIETARY'
 * @param activeBusinessId number id for the active business
 * @returns Note object
 */
function guestNotes(
  itemState: string,
  ticketGroup: core.TicketGroup,
  noteType: string,
  activeBusinessId: number
): dashboard.AuditedNote {
  let businesses;

  if (itemState === TicketGroupStateConstants.HELD) {
    businesses = oc(ticketGroup).ticketSubset[0].heldForGuestProfile.businessGuestProfile([]);
  } else if (itemState === TicketGroupStateConstants.LOCKED) {
    businesses = oc(
      ticketGroup
    ).ticketSubset[0].purchaseVerifyInfo.dinerGuestProfile.businessGuestProfile([]);
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const business = find(businesses, (business) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return safeToNumber(business.business!.id) === safeToNumber(activeBusinessId);
  })!;

  if (!isEmpty(business)) {
    return find(business.note, (note) => {
      return note.noteType === noteType && note.note !== '';
    }) as dashboard.AuditedNote;
  }

  return '' as any;
}

/**
 * Get a dietary or general GROUP LEVEL guest note object
 *
 * @param purchase PastPurchase
 * @param noteType string of 'GENERAL' or 'DIETARY'
 * @returns Note object
 */
function guestGroupNotes(
  itemState: string,
  ticketGroup: core.TicketGroup,
  noteType: string
): dashboard.AuditedNote {
  let businessGroup;

  if (
    itemState === TicketGroupStateConstants.HELD ||
    itemState === TicketGroupStateConstants.LOCKED
  ) {
    businessGroup = oc(ticketGroup).ticketSubset[0].heldForGuestProfile.businessGroupGuestProfile();
  }

  if (businessGroup) {
    return find(businessGroup.note, (groupNote) => {
      return groupNote.noteType === noteType && groupNote.note !== '';
    }) as dashboard.AuditedNote;
  }

  return '' as any;
}
